'use client';

import React from 'react';

import { useProfile } from 'src/context/providers/ProfileProvider';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import { useGetProviderClassificationsQuery } from 'src/api/redux';
import { useGetFilterSetsQuery } from 'src/api/redux/filterSets';
import type { Classification } from 'src/api/redux/enrichment/enrichmentApi';
import { CircularProgress, HorizontalScroller } from 'src/components/PulseUI';
import type { HorizontalScrollerHandle } from 'src/components/PulseUI';
import {
  formatSignalType,
  groupSignalsByType,
} from 'src/components/Profiles/shared/utils/signalGrouping';

const getClassificationDotColor = (classification: Classification) => {
  const signals = classification.signals ?? [];
  if (!signals.length) return 'bg-neutral-300';
  const matchedCount = signals.filter((s) => s.matched).length;
  if (matchedCount === signals.length) return 'bg-green-500';
  if (matchedCount > 0) return 'bg-amber-400';
  return 'bg-red-400';
};

const ProviderClassifications = () => {
  const { entityDetails } = useProfile();

  const [currentIndex, setCurrentIndex] = React.useState(0);
  const scrollerRef = React.useRef<HorizontalScrollerHandle>(null);
  const chipRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

  const { currentData, isLoading } = useGetProviderClassificationsQuery(
    { providerId: String(entityDetails?.id) },
    { skip: !entityDetails?.id }
  );

  const { data: filterSetsData } = useGetFilterSetsQuery({});

  const codeSetLabelMap = React.useMemo(() => {
    const map = new Map<number, string>();
    if (filterSetsData?.results) {
      for (const filterSet of filterSetsData.results) {
        map.set(filterSet.id, filterSet.label);
      }
    }
    return map;
  }, [filterSetsData?.results]);

  const content = currentData?.content;

  // Reset to first classification when provider changes (drawer reopens)
  React.useEffect(() => {
    setCurrentIndex(0);
    scrollerRef.current?.scrollToStart();
  }, [entityDetails?.id]);

  const handleChipClick = (index: number) => {
    setCurrentIndex(index);
    const chip = chipRefs.current[index];
    if (chip) {
      scrollerRef.current?.scrollToCenter(chip);
    }
  };

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center min-h-48 bg-white border border-neutral-200 rounded-xl p-4"
        data-testid="provider-classifications-loading"
      >
        <CircularProgress size="sm" color="neutral" />
      </div>
    );
  }

  const currentClassification = content?.[currentIndex];

  return (
    <div
      className="flex flex-col gap-3 bg-white border border-neutral-200 rounded-xl p-4"
      data-testid={
        content?.length
          ? 'provider-classifications'
          : 'provider-classifications-empty'
      }
    >
      <WidgetHeader fallbackTitle="Provider Classifications" />

      {!content?.length ? (
        <div className="flex items-center justify-center min-h-48">
          <p className="text-sm font-semibold text-neutral-500">
            Provider doesn&apos;t match any classifications
          </p>
        </div>
      ) : (
        <>
          {/* Classification chips with scroll arrows */}
          <HorizontalScroller
            ref={scrollerRef}
            className="gap-2"
            ariaLabelLeft="Scroll classifications left"
            ariaLabelRight="Scroll classifications right"
            testIdPrefix="classifications"
          >
            {content.map((item, index) => (
              <button
                key={index}
                ref={(el) => {
                  chipRefs.current[index] = el;
                }}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                  index === currentIndex
                    ? 'bg-sky-50 text-sky-700 border border-sky-200'
                    : 'text-neutral-500 hover:bg-sky-100'
                }`}
                onClick={() => handleChipClick(index)}
                aria-pressed={index === currentIndex}
                data-testid={`classification-chip-${index}`}
              >
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${getClassificationDotColor(
                    item
                  )}`}
                  aria-hidden="true"
                />
                {item.name}
              </button>
            ))}
          </HorizontalScroller>

          {/* Classification description */}
          {currentClassification?.description && (
            <p
              className="text-xs text-neutral-500"
              data-testid="classification-description"
            >
              {currentClassification.description}
            </p>
          )}

          {/* Grouped signal rows */}
          <div className="flex flex-col -mx-4">
            {groupSignalsByType(
              (currentClassification?.signals ?? []) as Array<{
                signal_type: string;
                matched: boolean;
              }>
            ).map((group) => (
              <div
                key={group.signalType}
                data-testid={`signal-group-${group.signalType}`}
              >
                <div
                  role="heading"
                  aria-level={4}
                  aria-label={`${group.label}: ${group.matchedCount} of ${group.totalCount} matched`}
                  className="flex items-center justify-between gap-2 px-4 py-2 bg-neutral-50 border-y border-neutral-100"
                  data-testid={`signal-group-header-${group.signalType}`}
                >
                  <span className="text-xs font-semibold text-neutral-700">
                    {group.label}
                  </span>
                  <span
                    className="text-xs font-medium text-neutral-400"
                    data-testid={`signal-group-match-count-${group.signalType}`}
                  >
                    {group.matchedCount}/{group.totalCount} matched
                  </span>
                </div>
                {group.signals.map((signal: any, idx: number) => {
                  const isFreshFaces =
                    signal.signal_type === 'fresh_faces';
                  const resolvedLabel = signal.code_set_id
                    ? codeSetLabelMap.get(signal.code_set_id) ??
                      formatSignalType(signal.signal_type)
                    : signal.payer_type ??
                      formatSignalType(signal.signal_type);
                  const op = (signal.operator ?? '').replace(/_/g, ' ');
                  const threshold = op
                    ? `${op.charAt(0).toUpperCase() + op.slice(1)} ${(signal.threshold ?? 0).toLocaleString()}${signal.metric === 'percentage' ? '%' : ` ${signal.metric ?? ''}`}`
                    : null;
                  const isRedundant = resolvedLabel === group.label;
                  const primaryLabel =
                    isRedundant && threshold ? threshold : resolvedLabel;

                  // Fresh faces signals only carry lookback_months — show that instead
                  if (isFreshFaces) {
                    return (
                      <div
                        key={idx}
                        className={`flex items-start justify-between gap-4 px-4 py-3 ${
                          idx < group.signals.length - 1
                            ? 'border-b border-neutral-100'
                            : ''
                        }`}
                        data-testid={`signal-row-${group.signalType}-${idx}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span
                            className={`h-2 w-2 shrink-0 rounded-full ${
                              signal.matched
                                ? 'bg-green-500'
                                : 'bg-red-400'
                            }`}
                            aria-label={
                              signal.matched ? 'matched' : 'not matched'
                            }
                          />
                          <div
                            className="font-medium text-sm text-neutral-900"
                            data-testid={`signal-label-${group.signalType}-${idx}`}
                          >
                            Last {signal.lookback_months ?? 3} months
                          </div>
                        </div>
                        <div
                          className={`text-sm font-semibold whitespace-nowrap shrink-0 ${
                            signal.matched
                              ? 'text-neutral-900'
                              : 'text-red-500'
                          }`}
                          data-testid={`signal-value-${group.signalType}-${idx}`}
                        >
                          {signal.matched ? 'Yes' : 'No'}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={idx}
                      className={`flex items-start justify-between gap-4 px-4 py-3 ${
                        idx < group.signals.length - 1
                          ? 'border-b border-neutral-100'
                          : ''
                      }`}
                      data-testid={`signal-row-${group.signalType}-${idx}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className={`h-2 w-2 shrink-0 rounded-full ${
                            signal.matched
                              ? 'bg-green-500'
                              : 'bg-red-400'
                          }`}
                          aria-label={
                            signal.matched ? 'matched' : 'not matched'
                          }
                        />
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <div
                            className="font-medium text-sm text-neutral-900"
                            data-testid={`signal-label-${group.signalType}-${idx}`}
                          >
                            {primaryLabel}
                          </div>
                          {!isRedundant && threshold && (
                            <div
                              className="text-xs text-neutral-400"
                              data-testid={`signal-threshold-${group.signalType}-${idx}`}
                            >
                              {threshold}
                            </div>
                          )}
                        </div>
                      </div>
                      {signal.actual_value != null && (
                        <div
                          className={`text-sm font-semibold whitespace-nowrap shrink-0 ${
                            signal.matched
                              ? 'text-neutral-900'
                              : 'text-red-500'
                          }`}
                          data-testid={`signal-value-${group.signalType}-${idx}`}
                        >
                          {signal.metric === 'percentage'
                            ? `${Math.round(signal.actual_value)}%`
                            : `${signal.actual_value.toLocaleString()} ${signal.metric ?? ''}`}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ProviderClassifications;
